import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { logActivity } from '../../services/adminMockData';
import { geocodeOne, calculateDistance as distanceM } from '../../services/locationService';
import {
  getAreasAdmin,
  createArea,
  updateArea,
  MAX_NEARBY_RADIUS_M,
} from '../../services/apiService';

const NL_LAT = [50, 54];
const NL_LNG = [3, 8];

const EMPTY = {
  name: '',
  code: '',
  address: '',
  city: '',
  lat: 52.0,
  lng: 5.0,
  radius: 1000,
  status: 'actief',
  description: '',
};

export default function AdminAreaEditPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  // Areas come from the backend so radius and status edits actually persist.
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  // Address → GPS lookup. The lat/lng inputs stay editable afterwards.
  const [addressQuery, setAddressQuery] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [foundAddress, setFoundAddress] = useState('');
  const [foundCoords, setFoundCoords] = useState(null);
  const [moveWarning, setMoveWarning] = useState('');
  const [lookupError, setLookupError] = useState('');
  // Shown as placeholder when no gebiedscode is stored yet (name initials).
  const [codeSuggestion, setCodeSuggestion] = useState('');
  // Only an address the admin typed is geocoded — opening an existing gebied
  // must not silently move its stored GPS point.
  const addressTyped = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getAreasAdmin()
      .then((rows) => {
        if (cancelled) return;
        setAreas(rows);
        const found = id ? rows.find((a) => a.id === id) : null;
        if (found) {
          setForm({ ...EMPTY, ...found, code: found.codeStored ? found.code : '' });
          setAddressQuery(found.address || '');
          setCodeSuggestion(found.code);
        }
        else if (id) setSaveError('Gebied niet gevonden.');
      })
      .catch((err) => {
        if (!cancelled) setSaveError(err?.message || 'Kon gebieden niet laden.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookupAddress = useCallback(async (query) => {
    const q = (query ?? '').trim();
    if (!q) return;
    setLookingUp(true);
    setLookupError('');
    try {
      const hit = await geocodeOne(q);
      if (!hit) {
        setFoundAddress('');
        setFoundCoords(null);
        setMoveWarning('');
        setLookupError('Adres niet gevonden — probeer een ander adres');
        return;
      }
      // Nominatim returns lat/lon as strings; geocodeOne has already parsed
      // them to numbers, and they are stored as numbers here too.
      const lat = Number(hit.lat.toFixed(6));
      const lng = Number(hit.lng.toFixed(6));

      // A wrong match can silently move a gebied kilometres away — that is how
      // Amsterdamsepoort ended up 7.9 km east of the actual shopping centre.
      // Warn when the result is far from where the area currently sits so the
      // admin can check before saving.
      setForm((prev) => {
        const hadPoint =
          Number.isFinite(Number(prev.lat)) && Number.isFinite(Number(prev.lng));
        if (hadPoint && !isNew) {
          const moved = Math.round(
            distanceM(Number(prev.lat), Number(prev.lng), lat, lng)
          );
          setMoveWarning(
            moved > 2000
              ? `Let op: dit adres ligt ${(moved / 1000).toFixed(1)} km van de huidige locatie van dit gebied. Controleer of dit klopt.`
              : ''
          );
        } else {
          setMoveWarning('');
        }
        return { ...prev, lat, lng };
      });

      setFoundAddress(hit.displayName);
      setFoundCoords({ lat, lng });
      setErrors((prev) => ({ ...prev, lat: undefined, lng: undefined }));
    } catch {
      setFoundAddress('');
      setLookupError('Adres zoeken mislukt — controleer je verbinding');
    } finally {
      setLookingUp(false);
    }
  }, [isNew]);

  // Debounced lookup so typing an address resolves without pressing the button.
  useEffect(() => {
    const q = addressQuery.trim();
    if (!addressTyped.current || q.length < 5) return;
    const t = setTimeout(() => lookupAddress(q), 900);
    return () => clearTimeout(t);
  }, [addressQuery, lookupAddress]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Naam is verplicht';
    const code = (form.code || '').trim();
    if (code && !/^[A-Z0-9-]{2,10}$/.test(code)) errs.code = '2–10 tekens: A–Z, 0–9 of -';
    if (code && areas.some((a) => a.id !== id && a.codeStored && a.code === code)) {
      errs.code = 'Deze gebiedscode is al in gebruik';
    }
    if (form.lat < NL_LAT[0] || form.lat > NL_LAT[1]) errs.lat = `Binnen NL: ${NL_LAT[0]}–${NL_LAT[1]}`;
    if (form.lng < NL_LNG[0] || form.lng > NL_LNG[1]) errs.lng = `Binnen NL: ${NL_LNG[0]}–${NL_LNG[1]}`;
    // The backend rejects a radius above 10 km.
    if (form.radius < 100 || form.radius > MAX_NEARBY_RADIUS_M) {
      errs.radius = `100–${MAX_NEARBY_RADIUS_M.toLocaleString('nl-NL')} m`;
    }

    const dup = areas.find(
      (a) => a.name.toLowerCase() === form.name.trim().toLowerCase() && a.id !== id
    );
    if (dup) errs.name = 'Naam moet uniek zijn';
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaveError('');
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const payload = {
      name: form.name.trim(),
      code: (form.code || codeSuggestion || '').trim(),
      address: addressQuery.trim(),
      city: form.city,
      lat: form.lat,
      lng: form.lng,
      radius: form.radius,
      status: form.status,
    };

    setSaving(true);
    try {
      if (isNew) {
        await createArea(payload);
        logActivity('Nieuw gebied aangemaakt', { area: payload.name });
      } else {
        await updateArea(id, payload);
        logActivity('Gebied bijgewerkt', {
          area: payload.name, radius: form.radius, status: form.status,
        });
      }
      navigate('/admin/areas');
    } catch (err) {
      setSaveError(err?.message || 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const streetEstimate = Math.round(form.radius / 50);

  return (
    <div>
      <header className="admin-page-head">
        <div>
          <button className="admin-link" onClick={() => navigate('/admin/areas')}>
            ← Terug naar gebieden
          </button>
          <h1 className="admin-page-title" style={{ marginTop: 6 }}>
            {isNew ? 'Nieuw gebied' : `Bewerk: ${form.name}`}
          </h1>
        </div>
      </header>

      <form onSubmit={submit} className="admin-card" style={{ maxWidth: 720 }}>
        <div className="admin-form-row">
          <div className="admin-field">
            <span className="admin-field__label">Naam</span>
            <input
              className="admin-input"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              required
            />
            {errors.name && <span style={{ color: '#DC2626', fontSize: 12 }}>{errors.name}</span>}
          </div>
          <div className="admin-field">
            <span className="admin-field__label">Gebiedscode</span>
            <input
              className="admin-input"
              maxLength={10}
              value={form.code}
              onChange={(e) => set('code', e.target.value.toUpperCase().replace(/\s+/g, ''))}
              placeholder={codeSuggestion ? `bijv. ${codeSuggestion}` : 'bijv. WGP'}
            />
            <span className="admin-field__hint">
              Interne code om gebieden te coderen/categoriseren. Zichtbaar voor
              admin en winkelier, niet voor bezoekers.
            </span>
            {errors.code && <span style={{ color: '#DC2626', fontSize: 12 }}>{errors.code}</span>}
          </div>
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Stad</span>
          <input
            className="admin-input"
            value={form.city}
            onChange={(e) => set('city', e.target.value)}
            required
          />
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Gebiedsadres</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              className="admin-input"
              style={{ flex: 1 }}
              value={addressQuery}
              placeholder="bijv. Gelderlandplein 1, Amsterdam"
              onChange={(e) => {
                addressTyped.current = true;
                setAddressQuery(e.target.value);
                setLookupError('');
                setMoveWarning('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  lookupAddress(addressQuery);
                }
              }}
            />
            <button
              type="button"
              className="admin-btn admin-btn--ghost admin-btn--small"
              onClick={() => lookupAddress(addressQuery)}
              disabled={lookingUp || !addressQuery.trim()}
            >
              {lookingUp ? 'Zoeken…' : 'Zoek'}
            </button>
          </div>
          {foundAddress && (
            <span className="admin-field__hint" style={{ color: '#166534' }}>
              Gevonden: {foundAddress}
              {foundCoords && (
                <>
                  {' '}
                  <span className="admin-mono">
                    ({foundCoords.lat.toFixed(5)}, {foundCoords.lng.toFixed(5)})
                  </span>
                </>
              )}
            </span>
          )}
          {moveWarning && (
            <span style={{ color: '#B45309', fontSize: 12, fontWeight: 600 }}>
              {moveWarning}
            </span>
          )}
          {lookupError && (
            <span style={{ color: '#DC2626', fontSize: 12 }}>{lookupError}</span>
          )}
          {!foundAddress && !lookupError && (
            <span className="admin-field__hint">
              Het adres van het gebied (bijv. het winkelcentrum). Wordt
              opgeslagen en aan winkeliers getoond; vult de GPS-velden
              hieronder automatisch in.
            </span>
          )}
        </div>

        <div className="admin-form-row">
          <div className="admin-field">
            <span className="admin-field__label">GPS Latitude</span>
            <input
              className="admin-input"
              type="number"
              step="0.0001"
              value={form.lat}
              onChange={(e) => set('lat', parseFloat(e.target.value))}
              required
            />
            <span className="admin-field__hint">Tussen {NL_LAT[0]} en {NL_LAT[1]}</span>
            {errors.lat && <span style={{ color: '#DC2626', fontSize: 12 }}>{errors.lat}</span>}
          </div>
          <div className="admin-field">
            <span className="admin-field__label">GPS Longitude</span>
            <input
              className="admin-input"
              type="number"
              step="0.0001"
              value={form.lng}
              onChange={(e) => set('lng', parseFloat(e.target.value))}
              required
            />
            <span className="admin-field__hint">Tussen {NL_LNG[0]} en {NL_LNG[1]}</span>
            {errors.lng && <span style={{ color: '#DC2626', fontSize: 12 }}>{errors.lng}</span>}
          </div>
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Radius — {form.radius.toLocaleString('nl-NL')} m</span>
          <input
            type="range"
            min={100}
            max={10000}
            step={100}
            value={form.radius}
            onChange={(e) => set('radius', parseInt(e.target.value, 10))}
            style={{ accentColor: '#E8621A' }}
          />
          <span className="admin-field__hint">Dekt ongeveer {streetEstimate} straten</span>
          {errors.radius && <span style={{ color: '#DC2626', fontSize: 12 }}>{errors.radius}</span>}
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Beschrijving</span>
          <textarea
            className="admin-input"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={3}
            placeholder="Optioneel — interne notities over dit gebied"
          />
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Status</span>
          <select
            className="admin-select"
            value={form.status}
            onChange={(e) => set('status', e.target.value)}
          >
            <option value="actief">Actief</option>
            <option value="inactief">Inactief</option>
          </select>
        </div>

        <div className="admin-field">
          <span className="admin-field__label">Kaart preview</span>
          <div style={{
            background: '#F1F5F9',
            border: '1px solid #E5E7EB',
            borderRadius: 12,
            padding: 14,
            fontSize: 13,
            color: '#4B5563',
            display: 'flex',
            gap: 14,
            alignItems: 'center',
          }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(232,98,26,0.4), rgba(232,98,26,0.1))',
              border: '2px solid #E8621A',
              flexShrink: 0,
            }} />
            <div>
              <div><strong>{form.name || 'Nieuw gebied'}</strong></div>
              <div>📍 {form.lat.toFixed(4)}, {form.lng.toFixed(4)}</div>
              <div>📏 Radius: {form.radius.toLocaleString('nl-NL')} m</div>
              <a
                href={`https://www.google.com/maps/?q=${form.lat},${form.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0EA5E9', fontWeight: 700 }}
              >
                Open in Google Maps →
              </a>
            </div>
          </div>
        </div>

        {saveError && (
          <div
            style={{
              background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B',
              borderRadius: 8, padding: '10px 12px', fontSize: 13, marginTop: 10,
            }}
          >
            {saveError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button type="button" className="admin-btn admin-btn--ghost" onClick={() => navigate('/admin/areas')}>
            Annuleren
          </button>
          <button type="submit" className="admin-btn admin-btn--orange" disabled={saving || loading}>
            {saving ? 'Opslaan…' : (isNew ? 'Aanmaken' : 'Wijzigingen opslaan')}
          </button>
        </div>
      </form>
    </div>
  );
}
